import { useQuery } from '@tanstack/react-query'
import range from 'lodash/range'
import { ProductIds } from '@mixtiles/web-backend-shared'
import { usePricing } from 'components/Pricing/PricingProvider'
import type { SKUProduct } from 'components/Pricing/types/checkout.types'
import { useCartDiscount } from 'features/cart/hooks/useCartDiscount'
import { useCheckoutPromoInputs } from 'features/cart/hooks/useCheckoutPromoInputs'
import { useGroupCheckoutPricing } from 'features/cart/hooks/useGroupCheckoutPricing'
import { useKeys } from 'services/KeysProvider'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import { useCartStore } from 'stores/cartStore/CartStoreProvider'
import { useMobxSelector } from 'utils/mobx/useMobxSelector'
import { formatPrice, roundDecimal } from 'utils/utils'
import type { UpsellWall } from '../upsellWall'

type UpsellWallPrice = {
  current: string
  original: string | null
}

// The cart's buyover counts every product toward its tiers, books included, while batch pricing sees only the
// tiles, so a tier the wall reaches outranks the batch's discount. A tier's `remaining` is its gap from the
// current cart. Checkout stays the source of truth.
function computeUpsellWallPrice({
  wallPrice,
  buyover,
}: {
  wallPrice: { priceBeforeDiscount: number; priceAfterDiscount: number }
  buyover: { tiers: { discountPercent: number; remaining: number }[] } | null
}): { fullPrice: number; discountedPrice: number | null } {
  const fullPrice = wallPrice.priceBeforeDiscount
  const crossedTiers =
    buyover?.tiers.filter((tier) => tier.remaining <= fullPrice) ?? []
  if (crossedTiers.length > 0) {
    const discountPercent = Math.max(
      ...crossedTiers.map((tier) => tier.discountPercent)
    )
    return {
      fullPrice,
      discountedPrice: fullPrice * (1 - discountPercent / 100),
    }
  }
  if (wallPrice.priceAfterDiscount < fullPrice) {
    return { fullPrice, discountedPrice: wallPrice.priceAfterDiscount }
  }
  return { fullPrice, discountedPrice: null }
}

type WallPrice = {
  tilesCount: number
  priceBeforeDiscount: number
  priceAfterDiscount: number
}

type WallPriceBreakdown = {
  tilesCount: number
  fullPrice: number
  discountedPrice: number | null
}

export function useUpsellWallPrices({
  tileStyle,
  offeredTilesCount,
}: {
  tileStyle: UpsellWall['tileStyle']
  offeredTilesCount: number
}) {
  const { pricingData, discounts, pricingVariantsIds, calculateBatchPricing } =
    usePricing()
  const { ipCountry } = useKeys()
  const { promocode } = useCheckoutPromoInputs()
  const groupPricing = useGroupCheckoutPricing()
  const buyover = groupPricing.data?.buyoverData ?? null
  const currency = pricingData.currency
  const { tileSize, materialType } = tileStyle
  const cartStore = useCartStore()
  const bagTileProducts = useMobxSelector(
    () =>
      cartStore
        .getPricingPayloads()
        .filter((payload) => payload.productType === PRODUCT_TYPES.CLASSIC)
        .flatMap((payload) => payload.products),
    []
  )
  const cartDiscount = useCartDiscount()
  const bagDiscountId = cartDiscount?.metadata.discountId

  return useQuery({
    queryKey: [
      'upsell-wall-price',
      tileSize,
      materialType,
      offeredTilesCount,
      ipCountry,
      pricingVariantsIds,
      promocode,
      bagTileProducts,
      bagDiscountId,
      buyover,
    ],
    // Every count the user can turn the wall's tiles down to, in one request, so a toggle reprices without loading.
    queryFn: async (): Promise<WallPriceBreakdown[]> => {
      const getWallPriceBreakdown = (
        wallPrice: WallPrice
      ): WallPriceBreakdown => ({
        tilesCount: wallPrice.tilesCount,
        ...computeUpsellWallPrice({ wallPrice, buyover }),
      })
      const tilesCounts = range(1, offeredTilesCount + 1)
      const getWallProduct = (tilesCount: number): SKUProduct => ({
        id: ProductIds.SKU_TILES,
        quantity: tilesCount,
        pricingProduct: { tileSize, materialType },
      })
      // PricingProvider is book-scoped on photo-book pages, so the tiles' product type is explicit.
      if (bagTileProducts.length === 0) {
        const wallPrices = await calculateBatchPricing(
          tilesCounts.map((tilesCount) => ({
            products: [getWallProduct(tilesCount)],
          })),
          PRODUCT_TYPES.CLASSIC,
          promocode || undefined
        )
        return tilesCounts.map((tilesCount, countIndex) => {
          const wallPrice = wallPrices[countIndex]
          if (!wallPrice) {
            throw new Error('Batch pricing returned no upsell wall price')
          }
          return getWallPriceBreakdown({ tilesCount, ...wallPrice })
        })
      }

      // Tiles are bundle-priced on the bag's whole count, so the wall costs what it adds to the bag's tiles.
      const [bagPrice, ...bagWithWallPrices] = await calculateBatchPricing(
        [
          { products: bagTileProducts, discountId: bagDiscountId },
          ...tilesCounts.map((tilesCount) => ({
            products: [...bagTileProducts, getWallProduct(tilesCount)],
            discountId: bagDiscountId,
          })),
        ],
        PRODUCT_TYPES.CLASSIC,
        promocode || undefined
      )
      if (!bagPrice) {
        throw new Error('Batch pricing returned no price for the bag tiles')
      }
      return tilesCounts.map((tilesCount, countIndex) => {
        const bagWithWallPrice = bagWithWallPrices[countIndex]
        if (!bagWithWallPrice) {
          throw new Error('Batch pricing returned no upsell wall price')
        }
        return getWallPriceBreakdown({
          tilesCount,
          priceBeforeDiscount: roundDecimal(
            bagWithWallPrice.priceBeforeDiscount - bagPrice.priceBeforeDiscount
          ),
          priceAfterDiscount: roundDecimal(
            bagWithWallPrice.priceAfterDiscount - bagPrice.priceAfterDiscount
          ),
        })
      })
    },
    select: (wallPriceBreakdowns): Record<number, UpsellWallPrice> =>
      Object.fromEntries(
        wallPriceBreakdowns.map(
          ({ tilesCount, fullPrice, discountedPrice }) => [
            tilesCount,
            {
              current: formatPrice(discountedPrice ?? fullPrice, currency),
              original:
                discountedPrice !== null
                  ? formatPrice(fullPrice, currency)
                  : null,
            },
          ]
        )
      ),
    // While the cart's group pricing loads, the batch's tiles-only discount would stand in for its buyover.
    enabled:
      !pricingData.isLoading && !discounts.isLoading && !groupPricing.isPending,
    staleTime: Infinity,
  })
}
